/**
 * Support Center RTK Query API
 * 
 * Provides hooks for support ticket operations.
 * Uses supportFirebase.js for all Firebase operations.
 */

import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createSupportTicket,
  getTherapistSupportTickets,
  getSupportTicket,
  addTherapistMessage,
} from "../../services/supportFirebase";

export const supportApi = createApi({
  reducerPath: "supportApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["SupportTickets", "SupportTicket"],
  endpoints: (builder) => ({
    // ========================================================================
    // QUERIES
    // ========================================================================

    /**
     * Get all support tickets for a therapist
     */
    getTherapistTickets: builder.query({
      async queryFn({ therapistId }) {
        try {
          const tickets = await getTherapistSupportTickets(therapistId);
          return { data: tickets };
        } catch (error) {
          console.error("[CK-7960] Error in getTherapistTickets:", {
            error: error.message,
            therapistId,
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "SupportTickets", id })),
              { type: "SupportTickets", id: "LIST" },
            ]
          : [{ type: "SupportTickets", id: "LIST" }],
    }),

    /**
     * Get a single support ticket by ID
     */
    getTicket: builder.query({
      async queryFn({ ticketId }) {
        try {
          const ticket = await getSupportTicket(ticketId);
          if (!ticket) {
            return { error: { message: "Ticket not found" } };
          }
          return { data: ticket };
        } catch (error) {
          console.error("[CK-7961] Error in getTicket:", {
            error: error.message,
            ticketId,
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { ticketId }) => [
        { type: "SupportTicket", id: ticketId },
      ],
    }),

    // ========================================================================
    // MUTATIONS
    // ========================================================================

    /**
     * Create a new support ticket
     */
    createTicket: builder.mutation({
      async queryFn({ clinicId, therapistId, therapistName, clinicName, subject, description, category }) {
        try {
          const ticket = await createSupportTicket({
            clinicId,
            therapistId,
            therapistName,
            clinicName,
            subject,
            description,
            category,
          });
          return { data: ticket };
        } catch (error) {
          console.error("[CK-7962] Error in createTicket:", {
            error: error.message,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "SupportTickets", id: "LIST" }],
    }),

    /**
     * Add a message to a support ticket (therapist)
     */
    addMessage: builder.mutation({
      async queryFn({ ticketId, senderId, senderName, content }) {
        try {
          const ticket = await addTherapistMessage(ticketId, {
            senderId,
            senderName,
            content,
          });
          return { data: ticket };
        } catch (error) {
          console.error("[CK-7963] Error in addMessage:", {
            error: error.message,
            ticketId,
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { ticketId }) => [
        { type: "SupportTicket", id: ticketId },
        { type: "SupportTickets", id: "LIST" },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetTherapistTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
  useAddMessageMutation,
} = supportApi;
